import React, { useState, useEffect, useCallback } from 'react';
import { AiEstimateNotice } from '@/components/AiEstimateNotice';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Leaf, Loader2, AlertTriangle, Info, RefreshCw,
  MousePointerClick
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animation constants to prevent re-renders from inline objects
const FADE_IN = { opacity: 1 };
const FADE_OUT = { opacity: 0 };
const SLIDE_UP = { opacity: 1, y: 0 };
const SLIDE_DOWN_INITIAL = { opacity: 0, y: 20 };

// Color scheme for micronutrients
const NUTRIENT_COLORS = {
  vitamin_a: { primary: '#f97316', secondary: '#fed7aa' },
  vitamin_c: { primary: '#eab308', secondary: '#fef08a' },
  vitamin_b12: { primary: '#ef4444', secondary: '#fecaca' },
  folate: { primary: '#22c55e', secondary: '#bbf7d0' },
  calcium: { primary: '#3b82f6', secondary: '#bfdbfe' },
  iron: { primary: '#a855f7', secondary: '#e9d5ff' },
  potassium: { primary: '#06b6d4', secondary: '#a5f3fc' },
  iodine: { primary: '#ec4899', secondary: '#fbcfe8' },
  magnesium: { primary: '#14b8a6', secondary: '#99f6e4' },
  phosphorus: { primary: '#8b5cf6', secondary: '#ddd6fe' }
};

const WellnessMarkersPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [markersData, setMarkersData] = useState(null);

  const fetchWellnessMarkers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/wellness-markers/analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarkersData(response.data);
    } catch (error) {
      toast.error('Failed to load wellness markers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWellnessMarkers();
  }, [fetchWellnessMarkers]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'deficient': return 'text-red-600';
      case 'low': return 'text-amber-600';
      case 'adequate': return 'text-emerald-600';
      case 'excess': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'deficient': return 'bg-red-50 border-red-200';
      case 'low': return 'bg-amber-50 border-amber-200';
      case 'adequate': return 'bg-emerald-50 border-emerald-200';
      case 'excess': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const handleMarkerClick = (markerId) => {
    navigate(`/myinsul/wellness-markers/${markerId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    My Wellness Markers
                  </h1>
                  <p className="text-xs text-muted-foreground">Micronutrient Analysis</p>
                  <AiEstimateNotice />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchWellnessMarkers}
                disabled={loading}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                {TIER_NAMES.wellness}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-green-500" />
          </div>
        ) : !markersData?.has_data ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <AlertTriangle className="w-16 h-16 mx-auto text-green-400 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Data Available</h2>
            <p className="text-muted-foreground">{markersData?.message || 'Start logging your meals to see wellness markers!'}</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Date Range & Stats */}
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground">
                Analysis period: {markersData.date_range?.start} to {markersData.date_range?.end}
              </p>
              <p className="text-sm text-muted-foreground">
                {markersData.days_analyzed} days analyzed • {markersData.total_entries} meals
              </p>
            </div>

            {/* Info Box - Click to view details */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <MousePointerClick className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-green-800 mb-1">Interactive Charts</h3>
                  <p className="text-sm text-green-700">
                    <strong>Click on any pie chart</strong> to see which foods from the past month were highest and lowest in that micronutrient. 
                    Hover over the chart to see your daily consumption compared to the recommended amount.
                  </p>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Deficient (&lt;50%)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>Low (50-80%)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span>Adequate (80-150%)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span>Excess (&gt;150%)</span>
              </div>
            </div>

            {/* Micronutrient Pie Charts Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {markersData.markers?.map((marker) => {
                const colors = NUTRIENT_COLORS[marker.id] || { primary: '#6b7280', secondary: '#e5e7eb' };
                const consumed = Math.min(marker.consumed, marker.recommended * 2);
                const remaining = Math.max(0, marker.recommended - consumed);
                
                const chartData = [
                  { name: 'Consumed', value: consumed },
                  { name: 'Remaining', value: remaining > 0 ? remaining : 0 }
                ];

                return (
                  <motion.div
                    key={marker.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleMarkerClick(marker.id)}
                    className={`bg-white rounded-2xl shadow-lg p-4 cursor-pointer border-2 transition-all hover:shadow-xl ${getStatusBg(marker.status)}`}
                    data-testid={`marker-${marker.id}`}
                  >
                    <h3 className="text-sm font-bold text-center mb-2 truncate" title={marker.name}>
                      {marker.name}
                    </h3>
                    
                    <div className="h-32 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={45}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            <Cell fill={colors.primary} />
                            <Cell fill={colors.secondary} />
                          </Pie>
                          <Tooltip 
                            content={({ active }) => {
                              if (active) {
                                return (
                                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm z-50">
                                    <p className="font-bold text-gray-800">{marker.name}</p>
                                    <p className="text-gray-600">
                                      <span className="font-medium">Daily Average:</span> {marker.consumed} {marker.unit}
                                    </p>
                                    <p className="text-gray-600">
                                      <span className="font-medium">Recommended:</span> {marker.recommended} {marker.unit}
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      {/* Percentage in center */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className={`text-lg font-bold ${getStatusColor(marker.status)}`}>
                          {Math.round(marker.percentage)}%
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Educational Content */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mt-8">
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Understanding Your Wellness Markers</h3>
                  <div className="text-sm text-gray-600 space-y-3">
                    <p>
                      <strong>Micronutrients</strong> are essential vitamins and minerals your body needs in small amounts 
                      for proper functioning. Unlike macronutrients (protein, carbs, fat), deficiencies in micronutrients 
                      can lead to various health issues.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-orange-50 p-3 rounded-lg">
                        <strong className="text-orange-700">Vitamin A & C</strong>
                        <p className="text-xs mt-1">Support immune function, skin health, and vision. Found in colorful fruits and vegetables.</p>
                      </div>
                      <div className="bg-red-50 p-3 rounded-lg">
                        <strong className="text-red-700">Vitamin B12 & Folate</strong>
                        <p className="text-xs mt-1">Essential for nerve function and DNA synthesis. B12 is mainly in animal products.</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <strong className="text-blue-700">Calcium & Phosphorus</strong>
                        <p className="text-xs mt-1">Build strong bones and teeth. Found in dairy, leafy greens, and fortified foods.</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <strong className="text-purple-700">Iron & Magnesium</strong>
                        <p className="text-xs mt-1">Iron carries oxygen in blood; Magnesium supports muscle and nerve function.</p>
                      </div>
                      <div className="bg-cyan-50 p-3 rounded-lg">
                        <strong className="text-cyan-700">Potassium & Iodine</strong>
                        <p className="text-xs mt-1">Potassium regulates blood pressure; Iodine supports thyroid function.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default WellnessMarkersPage;
